// Ouverture des PDF « mobile-safe » à l'échelle de l'app (webview Capacitor).
//
// PROBLÈME : dans la webview Capacitor (app iOS/Android), `window.open(blob:/data:)`
// et `<a download>` ne fonctionnent pas → AUCUN PDF généré par l'app ne s'ouvrait
// sur téléphone (gestion des actifs, horaire, paie, prévention, factures, etc.).
// Chaque écran réimplémente `const url = URL.createObjectURL(blob); window.open(url)`,
// donc ~40 fichiers étaient touchés.
//
// SOLUTION (JS pur → livrable via Capgo, AUCUN plugin natif) : un intercepteur
// global installé une seule fois au démarrage. Sur mobile NATIF uniquement :
//   • PDF (blob: de type pdf/sans type, ou data:application/pdf) → on émet
//     l'événement `pfm:open-pdf`, capté par <GlobalPdfViewer/> qui l'affiche dans
//     la VISIONNEUSE IN-APP (aperçu pdf.js plein écran + boutons Partager/Imprimer
//     + Fermer) — la même expérience que la remise de propriété ;
//   • fichier NON-PDF (blob: typé Excel/CSV/image) → on bascule sur l'API Web
//     Share (feuille de partage native) car pdf.js ne sait pas les afficher.
// Sur desktop / navigateur web : COMPORTEMENT INCHANGÉ (window.open ouvre l'onglet).
//
// Note user-gesture : afficher la visionneuse ne nécessite AUCUNE activation
// utilisateur (c'est juste un rendu in-app), donc la lecture asynchrone du blob
// (FileReader) ne pose pas de problème. Seul le partage natif des fichiers
// non-PDF exige l'activation : il est donc appelé SYNCHRONEMENT dans window.open,
// le blob étant déjà connu via un cache léger posé sur URL.createObjectURL.

function estNatif() {
  try {
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

// « Fenêtre » factice truthy : retournée pour que le repli <a download> des
// appelants ne se déclenche pas une fois qu'on a pris en charge l'ouverture.
function fenetreFactice() {
  return { closed: false, focus() {}, close() {} };
}

// Émet l'événement capté par <GlobalPdfViewer/> → visionneuse PDF in-app.
function afficherPdfInApp(base64OuDataUrl, filename) {
  window.dispatchEvent(new CustomEvent('pfm:open-pdf', {
    detail: { base64: base64OuDataUrl, filename: filename || 'document.pdf' },
  }));
}

// Partage natif d'un fichier non-PDF (Excel/CSV/image). Retourne true si déclenché.
// Doit être appelé SYNCHRONEMENT dans le user-gesture (window.open).
function partagerFichierNatif(blob, nomSouhaite) {
  try {
    if (!navigator.canShare) return false;
    const type = blob.type || 'application/octet-stream';
    const ext = (type.includes('sheet') || type.includes('excel')) ? 'xlsx'
      : type.includes('csv') ? 'csv'
      : type.includes('image') ? (type.split('/')[1] || 'png')
      : type.includes('pdf') ? 'pdf' : 'bin';
    const file = new File([blob], nomSouhaite || `document.${ext}`, { type });
    if (!navigator.canShare({ files: [file] })) return false;
    navigator.share({ files: [file], title: file.name }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}

let installe = false;

export function installerOuverturePdfMobile() {
  if (installe || typeof window === 'undefined' || !estNatif()) return;
  installe = true;

  // 1) Cache url(blob:) → Blob, pour retrouver le Blob d'origine de façon fiable.
  const blobs = new Map();
  const origCreate = URL.createObjectURL.bind(URL);
  const origRevoke = URL.revokeObjectURL.bind(URL);
  URL.createObjectURL = (obj) => {
    const url = origCreate(obj);
    try { if (obj instanceof Blob) blobs.set(url, obj); } catch {}
    return url;
  };
  URL.revokeObjectURL = (url) => {
    try { blobs.delete(url); } catch {}
    return origRevoke(url);
  };

  // 2) Intercepter window.open des PDF/fichiers (blob:/data:application/pdf).
  const origOpen = window.open ? window.open.bind(window) : null;
  window.open = function (url, ...rest) {
    try {
      if (typeof url === 'string') {
        // a) data:application/pdf → déjà une dataURL, visionneuse in-app directe.
        if (url.startsWith('data:application/pdf')) {
          afficherPdfInApp(url);
          return fenetreFactice();
        }
        // b) blob: → on retrouve le Blob via le cache.
        if (url.startsWith('blob:')) {
          const blob = blobs.get(url);
          if (blob) {
            const type = blob.type || '';
            const estPdf = type === '' || type.includes('pdf');
            if (estPdf) {
              // Lecture asynchrone en dataURL puis affichage in-app (pas d'activation requise).
              const reader = new FileReader();
              reader.onload = () => afficherPdfInApp(reader.result);
              reader.readAsDataURL(blob);
              return fenetreFactice();
            }
            // Fichier non-PDF → partage natif (synchrone, dans le user-gesture).
            if (partagerFichierNatif(blob)) return fenetreFactice();
          }
        }
      }
    } catch (e) {
      // En cas de souci, on retombe sur le comportement d'origine.
    }
    return origOpen ? origOpen(url, ...rest) : null;
  };

  // 3) Intercepter les téléchargements via <a download> (blob:/data:) que la
  //    WKWebView ignore. On capture le clic en amont : PDF → visionneuse in-app,
  //    autre fichier → partage natif. Couvre TOUS les écrans en <a download>
  //    (créés en JS ou écrits en JSX) sans les modifier un par un.
  document.addEventListener('click', function (ev) {
    try {
      const a = ev.target && ev.target.closest ? ev.target.closest('a[download]') : null;
      if (!a) return;
      const href = a.getAttribute('href') || a.href || '';
      const nom = a.getAttribute('download') || '';

      // data:application/pdf → dataURL directe.
      if (href.startsWith('data:application/pdf')) {
        ev.preventDefault();
        afficherPdfInApp(href, nom || 'document.pdf');
        return;
      }
      // Seuls les blob: nous concernent (les liens http/relatifs gardent leur comportement).
      if (!href.startsWith('blob:')) return;

      const blob = blobs.get(href);
      if (!blob) return; // blob inconnu (révoqué/externe) → laisser le navigateur gérer
      const type = blob.type || '';
      const estPdf = type.includes('pdf') || (type === '' && /\.pdf$/i.test(nom));
      if (estPdf) {
        ev.preventDefault();
        const reader = new FileReader();
        reader.onload = () => afficherPdfInApp(reader.result, nom || 'document.pdf');
        reader.readAsDataURL(blob);
        return;
      }
      // Fichier non-PDF connu (Excel/CSV/image) → partage natif (synchrone dans le clic).
      if (partagerFichierNatif(blob, nom)) ev.preventDefault();
    } catch (e) {
      // En cas de souci : comportement d'origine (on laisse le téléchargement se faire).
    }
  }, true); // phase de capture : on passe avant le déclenchement du téléchargement
}

// Ouvre un fichier par son URL (souvent distante : Azure/SAS, export async). Sur
// mobile NATIF, on récupère le fichier en blob puis on passe par window.open
// (intercepté par le shim → visionneuse PDF in-app / partage natif). Repli SÛR :
// si le fetch échoue (CORS, réseau…), on ouvre l'URL directement comme avant
// (ZÉRO régression). Sur desktop/web : ouverture directe, inchangé.
export async function ouvrirFichierMobile(url) {
  if (!url) return;
  if (!estNatif()) { window.open(url, '_blank'); return; }
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('HTTP ' + res.status);
    let blob = await res.blob();
    // URL pointant un .pdf mais type générique → retyper pour viser la visionneuse.
    if (/\.pdf(\?|#|$)/i.test(url) && !(blob.type || '').includes('pdf')) {
      blob = new Blob([blob], { type: 'application/pdf' });
    }
    window.open(URL.createObjectURL(blob), '_blank');
  } catch (e) {
    window.open(url, '_blank'); // repli : navigateur système (comportement d'avant)
  }
}
